/**
 * KalmTide Studio — bootstrap and chrome. Wires store, timeline, panels,
 * worklet preview, file menu (import/export/share/upload), cloud group, and
 * the transport. Visual identity: "calm night sky" (see style.css).
 */

import type { Preset } from "../../schema/types.ts";
import { isSegmentedLayer } from "../../schema/types.ts";
import { validatePreset } from "../../schema/validator.ts";
import { renderAll, type StereoBuffer } from "../dsp/index.ts";
import { compileRamp } from "../dsp/curves.ts";
import { encodeWavFloat32 } from "../export/wav.ts";
import { makeAmbienceResolver } from "../dsp/ambience.ts";
import { Store } from "./store.ts";
import { Timeline } from "./timeline.ts";
import { Panels } from "./panels.ts";
import { Preview } from "./preview.ts";
import { encodeShareLink, decodeShareLink } from "./sharelink.ts";
import { openBreathingOverlay } from "./breathing.ts";
import { openSolfeggioDialog } from "./solfeggio.ts";
import { setupCloud } from "../account/cloud.ts";

function starterPreset(): Preset {
  return {
    schema: "kalmtide/1",
    meta: {
      id: crypto.randomUUID(),
      name: "New Session",
      goal: "meditate",
      durationSec: 600,
      created: new Date().toISOString(),
    },
    allowFallback: true,
    layers: [
      {
        id: "bb1", type: "binaural", waveform: "sine", gainDb: -8,
        segments: [
          { t0: 0, t1: 120, carrierHz: [220, 220], beatHz: [10, 10], curve: "hold" },
          { t0: 120, t1: 480, carrierHz: [220, 200], beatHz: [10, 6], curve: "exp" },
          { t0: 480, t1: 600, carrierHz: [200, 200], beatHz: [6, 6], curve: "hold" },
        ],
      },
      { id: "noise1", type: "noise", color: "brown", gainDb: -18 },
      { id: "amb1", type: "ambience", asset: "rain_soft", gainDb: -20, loop: true },
    ],
    master: { gainDb: 0, fadeInSec: 5, fadeOutSec: 10, limiter: true },
  };
}

// ------------------------------------------------------------------- setup

let initial = starterPreset();
try {
  const fromLink = decodeShareLink(location.hash);
  if (fromLink) initial = fromLink;
} catch (err) {
  console.error(err);
}

const store = new Store(initial);
const preview = new Preview();
const userAssets = new Map<string, StereoBuffer>();

// ------------------------------------------------------------------ chrome

const app = document.getElementById("app")!;

const ORB_SVG = `
<svg class="orb" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="20" cy="20" r="4.4" fill="#D7E0FF"/>
  <circle class="ring ring1" cx="20" cy="20" r="9" stroke="#8FA8FF" stroke-width="2.2" opacity="0.85"/>
  <circle class="ring ring2" cx="20" cy="20" r="13.8" stroke="#6C85E0" stroke-width="1.8" opacity="0.55"/>
  <circle class="ring ring3" cx="20" cy="20" r="18.4" stroke="#4A5FA8" stroke-width="1.4" opacity="0.35"/>
</svg>`;

const PLAY_ICON = `<svg viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9.5-5.5L4 2.5z"/></svg>`;
const PAUSE_ICON = `<svg viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2.5" width="3.4" height="11" rx="1"/><rect x="9.6" y="2.5" width="3.4" height="11" rx="1"/></svg>`;

const RESTART_ICON = `<svg viewBox="0 0 16 16" fill="currentColor"><rect x="2.5" y="2.5" width="2.2" height="11" rx="1"/><path d="M13.5 2.5v11L5.8 8l7.7-5.5z"/></svg>`;
const VOLUME_ICON = `<svg viewBox="0 0 16 16" fill="currentColor"><path d="M2 6v4h2.8L9 13.4V2.6L4.8 6H2z"/><path d="M10.8 5.2a3.6 3.6 0 0 1 0 5.6V9.4a2.2 2.2 0 0 0 0-2.8V5.2z" opacity="0.7"/></svg>`;

const topbar = document.createElement("header");
topbar.className = "topbar";
topbar.innerHTML = `
  <a class="brand" href="./index.html" title="KalmTide home">${ORB_SVG}
    <span class="wordmark">Kalm<b>Tide</b></span>
    <span class="tagline">studio</span>
  </a>
  <div class="actions"></div>
`;

const transportBar = document.createElement("footer");
transportBar.className = "transport-bar";
transportBar.innerHTML = `
  <div class="tb-left">
    <span class="tb-title" id="wk-session-name"></span>
    <span class="tb-sub" id="wk-session-sub"></span>
  </div>
  <div class="tb-center">
    <button id="wk-restart" class="icon-btn" title="Back to start">${RESTART_ICON}</button>
    <button id="wk-play" class="play-btn" title="Play / pause">${PLAY_ICON}</button>
    <span class="tb-time" id="wk-time-cur">0:00</span>
    <input id="wk-seek" class="seek" type="range" min="0" max="1000" value="0" step="1" />
    <span class="tb-time" id="wk-time-total">0:00</span>
  </div>
  <div class="tb-right">
    <span class="beat-pill" id="wk-beat" style="display:none"><span class="dot"></span><span id="wk-beat-text"></span></span>
    <span class="volume">${VOLUME_ICON}<input id="wk-volume" class="vol" type="range" min="0" max="100" value="100" step="1" /></span>
  </div>
`;

const timelineWrap = document.createElement("div");
timelineWrap.className = "timeline-wrap";
const side = document.createElement("div");
side.className = "side";
const main = document.createElement("div");
main.className = "main";
// Library sidebar exists only when a backend is configured (offline-first).
const sidebar = document.createElement("aside");
sidebar.className = "sidebar";
if (window.KALMTIDE_CONFIG?.apiBase) main.append(sidebar);
main.append(timelineWrap, side);

const toasts = document.createElement("div");
toasts.id = "toasts";

app.append(topbar, main, transportBar, toasts);

const actions = topbar.querySelector<HTMLElement>(".actions")!;
const playBtn = transportBar.querySelector<HTMLButtonElement>("#wk-play")!;
const restartBtn = transportBar.querySelector<HTMLButtonElement>("#wk-restart")!;
const timeCur = transportBar.querySelector<HTMLElement>("#wk-time-cur")!;
const timeTotal = transportBar.querySelector<HTMLElement>("#wk-time-total")!;
const seek = transportBar.querySelector<HTMLInputElement>("#wk-seek")!;
const volume = transportBar.querySelector<HTMLInputElement>("#wk-volume")!;
const sessionName = transportBar.querySelector<HTMLElement>("#wk-session-name")!;
const sessionSub = transportBar.querySelector<HTMLElement>("#wk-session-sub")!;
const beatPill = transportBar.querySelector<HTMLElement>("#wk-beat")!;
const beatText = transportBar.querySelector<HTMLElement>("#wk-beat-text")!;

const timeline = new Timeline(timelineWrap, store);
const panels = new Panels(side, store);

function setStatus(text: string, isError = false): void {
  const toast = document.createElement("div");
  toast.className = "toast" + (isError ? " error" : "");
  toast.textContent = text;
  toasts.append(toast);
  while (toasts.children.length > 4) toasts.firstElementChild?.remove();
  const ttl = isError ? 9000 : 4000;
  setTimeout(() => {
    toast.classList.add("out");
    setTimeout(() => toast.remove(), 450);
  }, ttl);
}

/** Dropdown menu builder. */
function menu(label: string, items: Array<{ label: string; action: () => void } | "sep">): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "menu";
  const btn = document.createElement("button");
  btn.textContent = `${label} ▾`;
  const panel = document.createElement("div");
  panel.className = "menu-panel";
  for (const item of items) {
    if (item === "sep") {
      const sep = document.createElement("div");
      sep.className = "menu-sep";
      panel.append(sep);
      continue;
    }
    const el = document.createElement("button");
    el.className = "menu-item";
    el.textContent = item.label;
    el.addEventListener("click", () => {
      wrap.classList.remove("open");
      item.action();
    });
    panel.append(el);
  }
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    wrap.classList.toggle("open");
  });
  document.addEventListener("click", () => wrap.classList.remove("open"));
  wrap.append(btn, panel);
  return wrap;
}

// ---------------------------------------------------------------- preview

/**
 * The preset as heard: audition-muted layers removed. Mutes affect preview
 * and WAV bounce only — save/share/export-JSON always use the full preset.
 */
function effectivePreset(): Preset | null {
  if (!store.valid) return null;
  const preset = JSON.parse(JSON.stringify(store.state.preset)) as Preset;
  if (store.muted.size > 0) {
    preset.layers = preset.layers.filter((l) => !store.muted.has(l.id));
  }
  return preset;
}

// --------------------------------------------------------- cloud voices

/** clv:<voice>:<text> — AI voices synthesized by the backend (see /tts). */
const CLV_RE = /^clv:(\w+):(.+)$/s;
/** gm:<id>/<cue>.mp3 — pre-recorded guided meditations served from R2. */
const GM_RE = /^gm:([a-z0-9-]+\/\d+\.mp3)$/;
const cloudVoices = new Map<string, StereoBuffer>();

async function decodeToStereo(bytes: ArrayBuffer): Promise<StereoBuffer> {
  const ctx = new OfflineAudioContext(2, 1, preview.sampleRate);
  const decoded = await ctx.decodeAudioData(bytes);
  const left = decoded.getChannelData(0);
  const right = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left;
  return { left: new Float32Array(left), right: new Float32Array(right) };
}

/**
 * Fetch + decode every remote voice source (clv: AI voices, gm: guided
 * meditation cues) the preset references that we don't hold yet. Must run
 * before preview.load / offline render — the engine's asset resolver is
 * synchronous by design.
 */
async function ensureCloudVoices(preset: Preset): Promise<void> {
  const cfg = (window as { KALMTIDE_CONFIG?: { apiBase?: string } }).KALMTIDE_CONFIG ?? {};
  for (const layer of preset.layers) {
    if (layer.type !== "voice" || cloudVoices.has(layer.source)) continue;

    const gm = GM_RE.exec(layer.source);
    if (gm) {
      if (!cfg.apiBase) throw new Error("guided audio needs the cloud configured (config.js apiBase)");
      const res = await fetch(`${cfg.apiBase}/guided/${gm[1]}`);
      if (!res.ok) throw new Error(`guided audio unavailable (${res.status})`);
      const asset = await decodeToStereo(await res.arrayBuffer());
      cloudVoices.set(layer.source, asset);
      await preview.addAsset(layer.source, asset);
      continue;
    }

    const m = CLV_RE.exec(layer.source);
    if (!m) continue;
    if (!cloud) throw new Error("AI voices need the cloud configured (config.js apiBase)");
    if (!cloud.signedIn()) throw new Error("sign in to render AI voices");
    const wav = await cloud.ttsFetch(m[2]!, m[1]!);
    const asset = await decodeToStereo(wav);
    cloudVoices.set(layer.source, asset);
    await preview.addAsset(layer.source, asset);
  }
}

let loadedJson = "";
function syncPreview(): void {
  const preset = effectivePreset();
  if (!preset) return;
  const json = JSON.stringify(preset);
  if (json === loadedJson) return;
  loadedJson = json;
  void (async () => {
    try {
      await ensureCloudVoices(preset);
      await preview.load(preset);
    } catch (err) {
      loadedJson = ""; // allow retry after e.g. signing in
      setStatus(`voice: ${err instanceof Error ? err.message : String(err)}`, true);
    }
  })();
}

preview.onPosition = (sec) => store.setTransport(sec, store.state.playing);
preview.onEnded = () => store.setTransport(0, false);
preview.onError = (msg) => setStatus(`preview: ${msg}`, true);
timeline.onSeek = (sec) => {
  preview.seek(sec);
  store.setTransport(sec, store.state.playing);
};

store.subscribe((state, what) => {
  if (what === "preset" && state.errors.length > 0) {
    // Errors render inline in the panel; no toast spam while typing.
    return;
  }
  if (what === "preset") syncPreview();
});

// --------------------------------------------------------------- transport

playBtn.addEventListener("click", () => {
  void (async () => {
    if (store.state.playing) {
      preview.pause();
      store.setTransport(store.state.positionSec, false);
    } else {
      if (!store.valid) {
        setStatus("cannot play: fix the validation errors first", true);
        return;
      }
      syncPreview();
      await preview.play();
      store.setTransport(store.state.positionSec, true);
    }
  })();
});

restartBtn.addEventListener("click", () => {
  preview.seek(0);
  store.setTransport(0, store.state.playing);
});

let scrubbing = false;
seek.addEventListener("input", () => {
  scrubbing = true;
  const sec = (Number(seek.value) / 1000) * store.state.preset.meta.durationSec;
  timeCur.textContent = fmtTime(sec);
  paintSeek();
});
seek.addEventListener("change", () => {
  scrubbing = false;
  const sec = (Number(seek.value) / 1000) * store.state.preset.meta.durationSec;
  preview.seek(sec);
  store.setTransport(sec, store.state.playing);
});

const savedVolume = localStorage.getItem("kalmtide.volume");
if (savedVolume !== null) volume.value = savedVolume;
preview.setVolume(Number(volume.value) / 100);
paintRange(volume, Number(volume.value) / 100);
volume.addEventListener("input", () => {
  preview.setVolume(Number(volume.value) / 100);
  localStorage.setItem("kalmtide.volume", volume.value);
  paintRange(volume, Number(volume.value) / 100);
});

function fmtTime(sec: number): string {
  const t = Math.floor(sec);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

/** Fill the range track up to the thumb (webkit ranges don't do this natively). */
function paintRange(el: HTMLInputElement, frac: number): void {
  const pct = (frac * 100).toFixed(1);
  el.style.background = `linear-gradient(90deg, var(--accent) ${pct}%, var(--raised) ${pct}%)`;
}

function paintSeek(): void {
  paintRange(seek, Number(seek.value) / 1000);
}

store.subscribe((state, what) => {
  if (what !== "transport" && what !== "preset") return;
  playBtn.innerHTML = state.playing ? PAUSE_ICON : PLAY_ICON;
  document.body.classList.toggle("playing", state.playing);
  timeCur.textContent = fmtTime(state.positionSec);
  timeTotal.textContent = fmtTime(state.preset.meta.durationSec);
  sessionName.textContent = state.preset.meta.name;
  sessionSub.textContent = `${state.preset.meta.goal} · ${state.preset.layers.length} layers`;
  if (!scrubbing) {
    seek.value = String(
      Math.round((state.positionSec / Math.max(1e-9, state.preset.meta.durationSec)) * 1000),
    );
    paintSeek();
  }

  const layer = state.preset.layers.find(isSegmentedLayer);
  const t = state.positionSec;
  const seg = layer?.segments.find((s) => t >= s.t0 && t < s.t1);
  if (seg) {
    const b = compileRamp(seg.curve, seg.beatHz[0], seg.beatHz[1], seg.t0, seg.t1).valueAt(t);
    beatPill.style.display = "";
    beatText.textContent = `${b.toFixed(2)} Hz`;
  } else {
    beatPill.style.display = "none";
  }
  if (what === "transport") timeline.draw();
});

// ------------------------------------------------------------------- file

function download(name: string, blob: Blob): void {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

function importJson(): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".json,application/json";
  input.addEventListener("change", () => {
    void (async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const doc: unknown = JSON.parse(await file.text());
        const result = validatePreset(doc);
        if (!result.ok) throw new Error(result.errors.join("\n"));
        store.replacePreset(result.preset);
        setStatus(`imported ${file.name}`);
      } catch (err) {
        setStatus(`import failed: ${String(err)}`, true);
      }
    })();
  });
  input.click();
}

function exportJson(): void {
  if (!store.valid) return setStatus("cannot export: preset invalid", true);
  const name = store.state.preset.meta.name.replace(/\W+/g, "_").toLowerCase() || "preset";
  download(`${name}.json`, new Blob([JSON.stringify(store.state.preset, null, 2)], { type: "application/json" }));
}

function exportWav(): void {
  if (!store.valid) return setStatus("cannot export: preset invalid", true);
  setStatus("rendering WAV…");
  void (async () => {
    try {
      const preset = effectivePreset() ?? store.state.preset;
      await ensureCloudVoices(preset);
      await new Promise((r) => setTimeout(r, 16)); // let the toast paint
      const ambience = makeAmbienceResolver(preview.sampleRate);
      // WAV bounces what you hear — audition mutes applied.
      const buffer = renderAll(preset, {
        sampleRate: preview.sampleRate,
        assets: (ref, kind) => userAssets.get(ref) ?? cloudVoices.get(ref) ?? ambience(ref, kind),
      });
      const name = store.state.preset.meta.name.replace(/\W+/g, "_").toLowerCase() || "session";
      download(`${name}.wav`, new Blob([encodeWavFloat32(buffer, preview.sampleRate).slice()], { type: "audio/wav" }));
      setStatus("WAV exported");
    } catch (err) {
      setStatus(`render failed: ${String(err)}`, true);
    }
  })();
}

function uploadAudio(): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "audio/*";
  input.addEventListener("change", () => {
    void (async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const raw = await file.arrayBuffer();
        const ctx = new OfflineAudioContext(2, 1, preview.sampleRate);
        const decoded = await ctx.decodeAudioData(raw);
        const left = decoded.getChannelData(0);
        const right = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left;
        const asset: StereoBuffer = { left: new Float32Array(left), right: new Float32Array(right) };
        const ref = `user://${file.name}`;
        userAssets.set(ref, asset);
        await preview.addAsset(ref, asset);
        loadedJson = "";
        syncPreview();
        setStatus(`uploaded ${ref} (${decoded.duration.toFixed(1)} s) — use as a voice source`);
      } catch (err) {
        setStatus(`decode failed: ${String(err)}`, true);
      }
    })();
  });
  input.click();
}

function newSession(): void {
  if (!confirm("Start a new session? The current one is discarded unless saved or exported.")) return;
  store.replacePreset(starterPreset());
  setStatus("new session");
}

function newSolfeggio(): void {
  openSolfeggioDialog((preset) => {
    store.replacePreset(preset);
    setStatus(`${preset.meta.name} — ready to play`);
  });
}

function shareLink(): void {
  void (async () => {
    if (!store.valid) return setStatus("cannot share: preset invalid", true);
    const url = encodeShareLink(store.state.preset);
    history.replaceState(null, "", url);
    await navigator.clipboard.writeText(url);
    setStatus("share link copied to clipboard");
  })();
}

// Practice tool, not a file op — its own top-level button. Runs over
// whatever is playing, exactly like the app's pacer.
const breatheBtn = document.createElement("button");
breatheBtn.textContent = "Breathe";
breatheBtn.title = "Paced breathing over the playing session";
breatheBtn.addEventListener("click", openBreathingOverlay);

actions.append(
  breatheBtn,
  menu("File", [
    { label: "New session", action: newSession },
    { label: "New Solfeggio tone…", action: newSolfeggio },
    "sep",
    { label: "Import JSON…", action: importJson },
    { label: "Export JSON", action: exportJson },
    { label: "Export WAV", action: exportWav },
    "sep",
    { label: "Upload audio…", action: uploadAudio },
    { label: "Copy share link", action: shareLink },
  ]),
);

// ------------------------------------------------------------------ cloud

const cloudSlot = document.createElement("span");
cloudSlot.className = "cloud-group";
actions.append(cloudSlot);
const cloud = setupCloud(
  { header: cloudSlot, sidebar },
  {
    onStatus: setStatus,
    onLoadPreset: (preset) => store.replacePreset(preset),
    currentPreset: () => (store.valid ? (JSON.parse(JSON.stringify(store.state.preset)) as Preset) : null),
  },
);

// ------------------------------------------------------------------- boot

timeline.draw();
panels.render();
syncPreview();
store.setTransport(0, false); // paint the transport readouts

// Dev/test hook: lets the headless smoke test read state and drive flows the
// DOM can't easily express (download interception). Not a public API.
declare global {
  interface Window {
    __kalmtide?: { store: Store; preview: Preview };
  }
}
window.__kalmtide = { store, preview };
